import { useState } from "react";
import {
  Stack,
  Title,
  Text,
  Card,
  TextInput,
  Textarea,
  Button,
  Group,
  Alert,
  Container,
} from "@mantine/core";
import {
  IconMail,
  IconUser,
  IconMessage,
  IconCheck,
  IconX,
  IconEye,
} from "@tabler/icons-react";

export function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [emailRevealed, setEmailRevealed] = useState(false);

  const revealEmail = () => {
    setEmailRevealed(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      // Create mailto link with form data
      const subject = encodeURIComponent(
        formData.subject || "Contact from Portfolio Website"
      );
      const body = encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
      );
      const mailtoLink = `mailto:contact@thebrianjones.dev?subject=${subject}&body=${body}`;

      // Open default email client
      window.location.href = mailtoLink;

      setSubmitStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error("Error sending email:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        <Stack gap="md" ta="center">
          <Title order={1} style={{ color: "#ffffff" }}>
            Get In Touch
          </Title>
          <Text size="lg" c="gray.3" maw={600} mx="auto">
            I&apos;m always interested in discussing new opportunities,
            collaborations, or just connecting with fellow developers and
            engineering leaders.
          </Text>
        </Stack>

        <Card
          withBorder
          p="xl"
          style={{
            backgroundColor: "rgba(18, 18, 26, 0.8)",
            borderColor: "rgba(139, 92, 246, 0.4)",
          }}
        >
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <Group grow>
                <TextInput
                  label="Name"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={(e) =>
                    handleInputChange("name", e.currentTarget.value)
                  }
                  leftSection={<IconUser size={16} />}
                  required
                  styles={{
                    label: { color: "#ffffff" },
                    input: {
                      backgroundColor: "rgba(30, 30, 40, 0.8)",
                      border: "1px solid rgba(139, 92, 246, 0.3)",
                      color: "#ffffff",
                      "&:focus": {
                        borderColor: "rgba(139, 92, 246, 0.6)",
                      },
                    },
                  }}
                />
                <TextInput
                  label="Email"
                  placeholder="your.email@example.com"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    handleInputChange("email", e.currentTarget.value)
                  }
                  leftSection={<IconMail size={16} />}
                  required
                  styles={{
                    label: { color: "#ffffff" },
                    input: {
                      backgroundColor: "rgba(30, 30, 40, 0.8)",
                      border: "1px solid rgba(139, 92, 246, 0.3)",
                      color: "#ffffff",
                      "&:focus": {
                        borderColor: "rgba(139, 92, 246, 0.6)",
                      },
                    },
                  }}
                />
              </Group>

              <TextInput
                label="Subject"
                placeholder="What would you like to discuss?"
                value={formData.subject}
                onChange={(e) =>
                  handleInputChange("subject", e.currentTarget.value)
                }
                leftSection={<IconMessage size={16} />}
                styles={{
                  label: { color: "#ffffff" },
                  input: {
                    backgroundColor: "rgba(30, 30, 40, 0.8)",
                    border: "1px solid rgba(139, 92, 246, 0.3)",
                    color: "#ffffff",
                    "&:focus": {
                      borderColor: "rgba(139, 92, 246, 0.6)",
                    },
                  },
                }}
              />

              <Textarea
                label="Message"
                placeholder="Tell me about your project, opportunity, or just say hello..."
                value={formData.message}
                onChange={(e) =>
                  handleInputChange("message", e.currentTarget.value)
                }
                minRows={6}
                required
                styles={{
                  label: { color: "#ffffff" },
                  input: {
                    backgroundColor: "rgba(30, 30, 40, 0.8)",
                    border: "1px solid rgba(139, 92, 246, 0.3)",
                    color: "#ffffff",
                    "&:focus": {
                      borderColor: "rgba(139, 92, 246, 0.6)",
                    },
                  },
                }}
              />

              {submitStatus === "success" && (
                <Alert
                  icon={<IconCheck size={16} />}
                  color="green"
                  title="Email client opened!"
                >
                  Your default email application should have opened with the
                  message pre-filled. Please send the email to complete your
                  message.
                </Alert>
              )}

              {submitStatus === "error" && (
                <Alert
                  icon={<IconX size={16} />}
                  color="red"
                  title="Something went wrong"
                >
                  There was an issue opening your email client. Please try again
                  or contact me directly at contact@thebrianjones.dev
                </Alert>
              )}

              <Group justify="center" mt="md">
                <Button
                  type="submit"
                  size="lg"
                  variant="filled"
                  color="violet"
                  leftSection={<IconMail size={18} />}
                  loading={isSubmitting}
                  disabled={
                    !formData.name || !formData.email || !formData.message
                  }
                >
                  Send Message
                </Button>
              </Group>
            </Stack>
          </form>
        </Card>

        <Card
          withBorder
          p="md"
          style={{
            backgroundColor: "rgba(18, 18, 26, 0.6)",
            borderColor: "rgba(139, 92, 246, 0.2)",
          }}
        >
          <Stack gap="xs" ta="center">
            <Text size="sm" c="gray.4">
              Prefer a direct approach?
            </Text>
            {!emailRevealed ? (
              <Button
                variant="subtle"
                size="sm"
                color="violet"
                leftSection={<IconEye size={14} />}
                onClick={revealEmail}
                style={{ fontSize: "0.875rem" }}
              >
                Click to reveal email
              </Button>
            ) : (
              <Text size="sm" c="violet.4" fw={500}>
                contact@thebrianjones.dev
              </Text>
            )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
